import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, Store, Zap } from "lucide-react";
import { Reveal } from "./Reveal";

const IMG =
  "https://images.pexels.com/photos/11091171/pexels-photo-11091171.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const points = [
  { icon: Store, text: "A trusted local hub — your familiar neighborhood store." },
  { icon: Zap, text: "Supercharged by Zeroomile technology for instant fulfilment." },
  { icon: ShieldCheck, text: "A beacon of service, not just another pickup point." },
];

export const BrightspotStory = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section
      id="brightspot"
      ref={ref}
      data-testid="brightspot-section"
      className="relative mx-auto max-w-[1400px] px-6 py-24 md:px-12 md:py-36"
    >
      <div className="grid grid-cols-1 gap-12 md:grid-cols-12 md:gap-16">
        {/* Left — narrative */}
        <div className="md:col-span-5">
          <Reveal>
            <p className="mb-6 flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-brand-volt">
              <span className="h-px w-8 bg-brand-volt" /> The Network
            </p>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display text-6xl uppercase leading-[0.9] tracking-tight md:text-7xl">
              What is a
              <br />
              <span className="text-brand-volt">Brightspot</span>
              <span className="font-serif text-4xl italic text-brand-muted md:text-5xl"> ?</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 max-w-md text-lg leading-relaxed text-brand-muted">
              A <span className="font-serif italic text-brand-bone">Brightspot</span> is our name
              for a partner neighborhood store. Not just pickup points — they are{" "}
              <span className="text-brand-bone">beacons of service</span> in the community. When a
              customer sees a Brightspot, they see a trusted local hub empowered by Zeroomile.
            </p>
          </Reveal>

          <div className="mt-10 space-y-5">
            {points.map((p, i) => (
              <Reveal key={i} delay={0.15 + i * 0.08}>
                <div className="flex items-start gap-4">
                  <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-brand-line bg-brand-surface text-brand-volt">
                    <p.icon size={18} />
                  </span>
                  <p className="pt-1.5 text-base text-brand-bone/90">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* Right — beacon image */}
        <div className="md:col-span-7">
          <Reveal delay={0.1} y={60}>
            <div className="relative overflow-hidden rounded-tl-[120px] rounded-br-[120px] border border-white/10">
              <motion.img
                style={{ y: imgY }}
                src={IMG}
                alt="A lit neighborhood store glowing at night — a Brightspot"
                className="h-[380px] w-full scale-110 object-cover md:h-[560px]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/25 to-transparent" />

              {/* pulsing beacon marker */}
              <div className="absolute left-[38%] top-[32%]">
                <span className="relative flex h-4 w-4">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-brand-volt beacon-ring" />
                  <span className="relative inline-flex h-4 w-4 rounded-full bg-brand-volt ring-4 ring-brand-volt/30" />
                </span>
              </div>

              <div className="absolute bottom-6 left-6 rounded-full border border-white/20 bg-black/50 px-4 py-2 backdrop-blur-md">
                <span className="text-xs uppercase tracking-[0.2em] text-white">
                  Brightspot · Live
                </span>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
